import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Typography,
  Descriptions,
  Tag,
  Badge,
  Avatar,
  List,
  Input,
  Select,
  Space,
  Divider,
  Statistic,
  Progress,
  Alert,
  Tooltip,
  Empty,
  Modal,
  message,
  Spin,
  Tabs
} from 'antd';
import {
  ArrowLeftOutlined,
  BankOutlined,
  UserOutlined,
  TeamOutlined,
  CrownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  EditOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  DollarOutlined,
  StarOutlined,
  SafetyCertificateOutlined,
  RobotOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { organizationService } from '../services/organizationService';
import { userService } from '../services/userService';
import { subscriptionPlansService } from '../services/subscriptionPlansService';
import CallSettings from './CallSettings';
import AISettingsForm from './AISettingsForm';
import WhatsAppSettingsForm from './WhatsAppSettingsForm';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const OrganizationDetail = ({ organizationId, onBack }) => {
  const [organization, setOrganization] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userFilters, setUserFilters] = useState({
    search: '',
    role: 'all',
    status: 'all'
  });
  const [planModalVisible, setPlanModalVisible] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [selectedNewPlan, setSelectedNewPlan] = useState(null);
  const [selectedTenantAdmin, setSelectedTenantAdmin] = useState(null);
  const [tenantAdminStats, setTenantAdminStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Fetch tenant admin statistics
  const fetchTenantAdminStats = async (adminId) => {
    setStatsLoading(true);
    try {
      // Fetch leads, deals, and users data for this tenant admin
      const response = await organizationService.getTenantAdminStats(organizationId, adminId);
      if (response.success) {
        setTenantAdminStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch tenant admin stats:', error);
      setTenantAdminStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  // Handle tenant admin selection
  const handleSelectTenantAdmin = (admin) => {
    setSelectedTenantAdmin(admin);
    fetchTenantAdminStats(admin._id);
  };

  // Fetch organization details
  const fetchOrganization = async () => {
    setLoading(true);
    try {
      const response = await organizationService.getOrganization(organizationId);
      if (response.success) {
        setOrganization(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for this organization
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      // Get all users and filter on frontend to avoid backend filtering issues
      const response = await userService.getUsers({
        hierarchical: 'true'
      });
      if (response.success) {
        // Flatten hierarchical structure - include both parents and children
        const flatUsers = [];
        const hierarchicalUsers = response.data.users || [];
        
        console.log('Fetched all users:', hierarchicalUsers);
        console.log('Filtering for organization:', organization?._id);
        
        hierarchicalUsers.forEach(user => {
          // Check if user belongs to this organization
          if (user.tenantId === organization?._id) {
            // Add the parent user
            flatUsers.push({
              ...user,
              name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
            });
            
            // Add all children users (excluding duplicates)
            if (user.children && user.children.length > 0) {
              user.children.forEach(child => {
                // Only add child if it's not a duplicate of the parent
                if (child._id !== user._id) {
                  flatUsers.push({
                    ...child,
                    name: `${child.firstName || ''} ${child.lastName || ''}`.trim() || child.email
                  });
                }
              });
            }
          }
        });
        
        console.log('Filtered users for organization:', flatUsers);
        setUsers(flatUsers);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchOrganization();
      fetchAvailablePlans();
    }
  }, [organizationId]);

  useEffect(() => {
    if (organization) {
      fetchUsers();
    }
  }, [organization]);

  // Apply filters on the frontend
  useEffect(() => {
    if (organization) {
      fetchUsers();
    }
  }, [userFilters]);

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    let matches = true;
    
    if (userFilters.search) {
      const searchLower = userFilters.search.toLowerCase();
      matches = matches && (
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower)
      );
    }
    
    if (userFilters.role !== 'all') {
      matches = matches && user.role === userFilters.role;
    }
    
    if (userFilters.status !== 'all') {
      const isActive = user.isActive || user.status === 'active';
      matches = matches && (
        (userFilters.status === 'active' && isActive) ||
        (userFilters.status === 'inactive' && !isActive)
      );
    }
    
    return matches;
  });

  // Fetch available subscription plans from database
  const fetchAvailablePlans = async () => {
    setPlansLoading(true);
    try {
      const response = await subscriptionPlansService.getPlans({ status: 'active' });
      if (response.success) {
        setAvailablePlans(response.data.plans || []);
      }
    } catch (error) {
      console.error('Failed to fetch subscription plans:', error);
      message.error('Failed to load subscription plans');
    } finally {
      setPlansLoading(false);
    }
  };

  // Handle plan change
  const handleChangePlan = async () => {
    if (!selectedNewPlan) {
      message.warning('Please select a plan');
      return;
    }

    setChangingPlan(true);
    try {
      // Use planType directly from the subscription plan
      const planValue = selectedNewPlan.planType;

      console.log('Changing plan:', { 
        planName: selectedNewPlan.name,
        planType: planValue 
      });

      const response = await organizationService.updatePlan(organizationId, planValue, selectedNewPlan._id);
      if (response.success) {
        message.success('Subscription plan updated successfully');
        setPlanModalVisible(false);
        setSelectedNewPlan(null);
        // Refresh organization data
        fetchOrganization();
      }
    } catch (error) {
      console.error('Failed to update plan:', error);
      message.error(error.message || 'Failed to update subscription plan');
    } finally {
      setChangingPlan(false);
    }
  };

  // Get plan details. organization.plan stores the SubscriptionPlan.planType
  // value (free/trial/basic/premium/professional/enterprise), set by the
  // PATCH /:id/plan route. We match the active plan list by planType.
  const getPlanDetails = (plan) => {
    const fallback = {
      name: plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : 'No plan',
      price: '—',
      period: '',
      color: '#8c8c8c',
      icon: <SafetyCertificateOutlined />,
      features: [],
      annualDiscountPercent: 0,
      trialDays: 0,
      coupons: [],
      limits: { users: '—', leads: '—', deals: '—', storage: '—', integrations: '—' },
    };
    if (!plan) return fallback;

    const dbPlan = availablePlans.find((p) => p.planType === plan);
    if (!dbPlan) return fallback;

    const userCount = users.length || 1;
    const basePrice = Number(dbPlan.price) || 0;
    const totalPrice = dbPlan.pricingType === 'per_user' ? basePrice * userCount : basePrice;
    const priceDisplay = basePrice === 0
      ? 'Free'
      : dbPlan.pricingType === 'per_user'
        ? `₹${basePrice.toLocaleString('en-IN')} × ${userCount} users = ₹${totalPrice.toLocaleString('en-IN')}`
        : `₹${totalPrice.toLocaleString('en-IN')}`;

    const fmtLimit = (n, suffix = '') =>
      n === -1 ? 'Unlimited' : `${Number(n || 0).toLocaleString('en-IN')}${suffix}`;

    return {
      name: dbPlan.name,
      planType: dbPlan.planType,
      price: priceDisplay,
      basePrice,
      totalPrice,
      pricingType: dbPlan.pricingType,
      userCount,
      period:
        dbPlan.billingPeriod === 'monthly' ? 'per month' :
        dbPlan.billingPeriod === 'yearly' ? 'per year' :
        dbPlan.billingPeriod === 'lifetime' ? 'lifetime' : 'Forever',
      color: dbPlan.metadata?.color || '#1890ff',
      icon: dbPlan.metadata?.icon === 'crown' ? <CrownOutlined /> :
            dbPlan.metadata?.icon === 'star' ? <StarOutlined /> :
            <SafetyCertificateOutlined />,
      features: dbPlan.features?.map((f) => f.name || f.description || String(f)) || [],
      annualDiscountPercent: dbPlan.annualDiscountPercent || 0,
      trialDays: dbPlan.trialDays || 0,
      coupons: dbPlan.coupons || [],
      limits: {
        users: fmtLimit(dbPlan.limits?.maxUsers),
        leads: fmtLimit(dbPlan.limits?.maxLeads),
        deals: fmtLimit(dbPlan.limits?.maxDeals),
        storage: fmtLimit(dbPlan.limits?.maxStorage, ' GB'),
        integrations: fmtLimit(dbPlan.limits?.maxIntegrations),
      },
      isPopular: dbPlan.isPopular,
    };
  };

  // Get user role color
  const getRoleColor = (role) => {
    const colors = {
      tenant_admin: 'red',
      crm_user: 'blue',
      admin: 'red',
      user: 'blue'
    };
    return colors[role] || 'default';
  };

  // Calculate usage statistics
  const calculateUsage = () => {
    if (!organization || !users.length) return null;
    
    const activeUsers = users.filter(u => u.isActive || u.status === 'active').length;
    const totalUsers = users.length;
    
    // Use organization settings if available, otherwise fallback to plan limits
    const settings = organization.settings || {};
    const maxUsers = settings.maxUsers || 10;
    const maxLeads = settings.maxLeads || 100;
    const maxDeals = settings.maxDeals || 100;
    
    const userUsagePercent = (totalUsers / maxUsers) * 100;
    const leadsUsagePercent = (organization.stats?.totalLeads || 0) / maxLeads * 100;
    const dealsUsagePercent = (organization.stats?.totalDeals || 0) / maxDeals * 100;
    
    return {
      users: {
        current: totalUsers,
        active: activeUsers,
        max: maxUsers,
        percentage: Math.min(userUsagePercent, 100)
      },
      leads: {
        current: organization.stats?.totalLeads || 0,
        max: maxLeads,
        percentage: Math.min(leadsUsagePercent, 100)
      },
      deals: {
        current: organization.stats?.totalDeals || 0,
        max: maxDeals,
        percentage: Math.min(dealsUsagePercent, 100)
      }
    };
  };

  if (loading || !organization) {
    return <div>Loading...</div>;
  }

  const planDetails = getPlanDetails(organization.plan);
  const usage = calculateUsage();
  const tenantAdmins = filteredUsers.filter(u => u.role === 'tenant_admin' || u.role === 'admin');
  const crmUsers = filteredUsers.filter(u => u.role === 'crm_user' || u.role === 'user');

  return (
    <div>
      {/* Back Button */}
      <div style={{ marginBottom: 16 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={onBack}
          size="large"
        >
          Back to Organizations
        </Button>
      </div>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          {organization.name}
        </Title>
        <Text type="secondary">Organization Details & Management</Text>
      </div>

      {/* Tabs for Basic Info and Call Settings */}
      <Tabs
        items={[
          {
            key: 'basic-info',
            label: 'Basic Info',
            children: (
              <Row gutter={24}>
        {/* Column 1: Organization Details */}
        <Col xs={24} lg={8}>
          <Card title="Organization Information" style={{ marginBottom: 16 }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Avatar 
                  size={80} 
                  icon={<BankOutlined />} 
                  style={{ backgroundColor: planDetails.color, marginBottom: 16 }}
                />
                <Title level={3} style={{ margin: 0 }}>
                  {organization.name}
                </Title>
                <Text type="secondary">{organization.subdomain}</Text>
              </div>

              <Descriptions column={1} size="small">
                <Descriptions.Item label="Status">
                  <Badge
                    status={organization.isActive ? 'success' : 'error'}
                    text={organization.isActive ? 'Active' : 'Inactive'}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Plan">
                  <Tag color={planDetails.color} icon={planDetails.icon}>
                    {planDetails.name}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  <Space>
                    <CalendarOutlined />
                    {new Date(organization.createdAt).toLocaleDateString()}
                  </Space>
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              <div>
                <Title level={5}>Contact Information</Title>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <MailOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                    <Text>{organization.contact?.email}</Text>
                  </div>
                  <div>
                    <PhoneOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                    <Text>{organization.contact?.phone || 'Not provided'}</Text>
                  </div>
                </Space>
              </div>

              <Divider />

              <div>
                <Title level={5}>Usage Statistics</Title>
                
                {/* Organization-wide stats */}
                {!selectedTenantAdmin && usage && (
                  <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Organization Total</Text>
                    <div>
                      <Text>Users: {usage.users.current} / {usage.users.max}</Text>
                      <Progress 
                        percent={usage.users.percentage} 
                        size="small"
                        status={usage.users.percentage > 90 ? 'exception' : 'normal'}
                      />
                    </div>
                    {usage.leads.max !== 'Unlimited' && (
                      <div>
                        <Text>Leads: {usage.leads.current} / {usage.leads.max}</Text>
                        <Progress 
                          percent={usage.leads.percentage} 
                          size="small"
                          status={usage.leads.percentage > 90 ? 'exception' : 'normal'}
                        />
                      </div>
                    )}
                    {usage.deals.max !== 'Unlimited' && (
                      <div>
                        <Text>Deals: {usage.deals.current} / {usage.deals.max}</Text>
                        <Progress 
                          percent={usage.deals.percentage} 
                          size="small"
                          status={usage.deals.percentage > 90 ? 'exception' : 'normal'}
                        />
                      </div>
                    )}
                  </Space>
                )}

                {/* Tenant Admin Selection */}
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Select Tenant Admin to view their data</Text>
                  <Select
                    style={{ width: '100%', marginTop: 8 }}
                    placeholder="Choose a tenant admin..."
                    value={selectedTenantAdmin?._id || undefined}
                    onChange={(adminId) => {
                      const admin = tenantAdmins.find(u => u._id === adminId);
                      if (admin) handleSelectTenantAdmin(admin);
                    }}
                    allowClear
                    onClear={() => {
                      setSelectedTenantAdmin(null);
                      setTenantAdminStats(null);
                    }}
                  >
                    {tenantAdmins.map(admin => (
                      <Option key={admin._id} value={admin._id}>
                        {admin.name} ({admin.email})
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* Tenant Admin Stats */}
                {selectedTenantAdmin && (
                  <Spin spinning={statsLoading}>
                    {tenantAdminStats && (
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Divider style={{ margin: '12px 0' }} />
                        <Text strong style={{ fontSize: '12px' }}>
                          Data for: {selectedTenantAdmin.name}
                        </Text>
                        <div>
                          <Text>Users: {tenantAdminStats.users || 0}</Text>
                        </div>
                        <div>
                          <Text>Leads: {tenantAdminStats.leads || 0}</Text>
                        </div>
                        <div>
                          <Text>Deals: {tenantAdminStats.deals || 0}</Text>
                        </div>
                      </Space>
                    )}
                  </Spin>
                )}
              </div>
            </Space>
          </Card>
        </Col>

        {/* Column 2: Users List */}
        <Col xs={24} lg={8}>
          <Card 
            title="Users Management" 
            extra={
              <Badge count={filteredUsers.length} style={{ backgroundColor: '#52c41a' }} />
            }
            style={{ marginBottom: 16 }}
          >
            {/* User Filters */}
            <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
              <Input
                placeholder="Search users..."
                prefix={<SearchOutlined />}
                value={userFilters.search}
                onChange={(e) => setUserFilters(prev => ({ ...prev, search: e.target.value }))}
                allowClear
              />
              <Row gutter={8}>
                <Col span={12}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Role"
                    value={userFilters.role}
                    onChange={(value) => setUserFilters(prev => ({ ...prev, role: value }))}
                  >
                    <Option value="all">All Roles</Option>
                    <Option value="tenant_admin">Tenant Admin</Option>
                    <Option value="crm_user">CRM User</Option>
                  </Select>
                </Col>
                <Col span={12}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Status"
                    value={userFilters.status}
                    onChange={(value) => setUserFilters(prev => ({ ...prev, status: value }))}
                  >
                    <Option value="all">All Status</Option>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </Col>
              </Row>
            </Space>

            {/* Tenant Admins */}
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>
                <TeamOutlined style={{ marginRight: 8, color: '#ff4d4f' }} />
                Tenant Admins ({tenantAdmins.length})
              </Title>
              <List
                size="small"
                dataSource={tenantAdmins}
                loading={usersLoading}
                locale={{ emptyText: <Empty description="No tenant admins found" /> }}
                renderItem={(user) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar 
                          icon={<UserOutlined />} 
                          style={{ backgroundColor: '#ff4d4f' }}
                        />
                      }
                      title={
                        <Space>
                          <Text strong>{user.name}</Text>
                          <Tag color={getRoleColor(user.role)} size="small">
                            {user.role?.replace('_', ' ').toUpperCase()}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size="small">
                          <Text type="secondary">{user.email}</Text>
                          <Badge
                            status={user.isActive ? 'success' : 'error'}
                            text={user.isActive ? 'Active' : 'Inactive'}
                          />
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>

            <Divider />

            {/* CRM Users */}
            <div>
              <Title level={5}>
                <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                CRM Users ({crmUsers.length})
              </Title>
              <List
                size="small"
                dataSource={crmUsers}
                loading={usersLoading}
                locale={{ emptyText: <Empty description="No CRM users found" /> }}
                renderItem={(user) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar 
                          icon={<UserOutlined />} 
                          style={{ backgroundColor: '#1890ff' }}
                        />
                      }
                      title={
                        <Space>
                          <Text strong>{user.name}</Text>
                          <Tag color={getRoleColor(user.role)} size="small">
                            {user.role?.replace('_', ' ').toUpperCase()}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size="small">
                          <Text type="secondary">{user.email}</Text>
                          <Badge
                            status={user.isActive ? 'success' : 'error'}
                            text={user.isActive ? 'Active' : 'Inactive'}
                          />
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          </Card>
        </Col>

        {/* Column 3: Subscription Plan */}
        <Col xs={24} lg={8}>
          <Card 
            title="Subscription Plan" 
            extra={
              <Tag color={planDetails.color} icon={planDetails.icon}>
                {planDetails.name}
              </Tag>
            }
            style={{ marginBottom: 16 }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* Plan Header */}
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '48px', color: planDetails.color }}>
                  {planDetails.icon}
                </div>
                <Title level={3} style={{ margin: '16px 0 8px 0', color: planDetails.color }}>
                  {planDetails.name}
                </Title>
                <div>
                  <Text style={{ fontSize: '32px', fontWeight: 'bold', color: planDetails.color }}>
                    {planDetails.price}
                  </Text>
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    {planDetails.period}
                  </Text>
                </div>
              </div>

              <Divider />

              {/* Plan Features */}
              <div>
                <Title level={5}>Features Included</Title>
                <List
                  size="small"
                  dataSource={planDetails.features}
                  renderItem={(feature) => (
                    <List.Item>
                      <Space>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        <Text>{feature}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </div>

              <Divider />

              {/* Plan Limits */}
              <div>
                <Title level={5}>Plan Limits</Title>
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="Max Users">
                    <Text strong>{planDetails.limits.users}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Max Leads">
                    <Text strong>{planDetails.limits.leads}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Max Deals">
                    <Text strong>{planDetails.limits.deals}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Storage">
                    <Text strong>{planDetails.limits.storage}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Integrations">
                    <Text strong>{planDetails.limits.integrations}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </div>

              {/* Extras: trial days, annual discount, coupons */}
              {(planDetails.planType === 'trial' && planDetails.trialDays > 0) ||
               planDetails.annualDiscountPercent > 0 ||
               planDetails.coupons?.length > 0 ? (
                <div style={{ marginTop: 16 }}>
                  <Title level={5}>Plan extras</Title>
                  <Space size={[6, 6]} wrap>
                    {planDetails.planType === 'trial' && planDetails.trialDays > 0 && (
                      <Tag color="blue">{planDetails.trialDays}-day trial</Tag>
                    )}
                    {planDetails.annualDiscountPercent > 0 && (
                      <Tag color="green">{planDetails.annualDiscountPercent}% off annually</Tag>
                    )}
                    {(planDetails.coupons || []).map((c) => (
                      <Tag key={c._id || c.code} color="purple">
                        {c.code} · −{c.discountPercent}%
                      </Tag>
                    ))}
                  </Space>
                </div>
              ) : null}

              {/* Usage Alert */}
              {usage && usage.users.percentage > 80 && (
                <Alert
                  message="Usage Warning"
                  description={`This organization is using ${Math.round(usage.users.percentage)}% of their user limit. Consider upgrading the plan.`}
                  type="warning"
                  showIcon
                />
              )}

              {/* Change Plan Button */}
              <div style={{ marginTop: 16 }}>
                <Button 
                  type="primary" 
                  block 
                  icon={<EditOutlined />}
                  onClick={() => setPlanModalVisible(true)}
                >
                  Change Subscription Plan
                </Button>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
            ),
          },
          {
            key: 'call-settings',
            label: 'Call Settings',
            children: (
              <CallSettings organizationId={organizationId} />
            ),
          },
          {
            key: 'ai-settings',
            label: (
              <span>
                <RobotOutlined style={{ marginRight: 8 }} />
                AI Settings
              </span>
            ),
            children: (
              <AISettingsForm organizationId={organizationId} onSave={fetchOrganization} />
            ),
          },
          {
            key: 'whatsapp-settings',
            label: (
              <span>
                <MessageOutlined style={{ marginRight: 8 }} />
                WhatsApp Settings
              </span>
            ),
            children: (
              <WhatsAppSettingsForm organizationId={organizationId} onSave={fetchOrganization} />
            ),
          },
        ]}
      />

      {/* Plan Change Modal */}
      <Modal
        title="Change Subscription Plan"
        open={planModalVisible}
        onCancel={() => {
          setPlanModalVisible(false);
          setSelectedNewPlan(null);
        }}
        onOk={handleChangePlan}
        confirmLoading={changingPlan}
        okText="Change Plan"
        cancelText="Cancel"
        width={800}
      >
        {plansLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>Loading subscription plans...</div>
          </div>
        ) : (
          <div>
            <Alert
              message="Current Plan"
              description={`${organization?.name} is currently on ${getPlanDetails(organization?.plan).name}`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Title level={5}>Select New Plan:</Title>
            <Row gutter={16}>
              {availablePlans.map((plan) => {
                const isSelected = selectedNewPlan?._id === plan._id;
                const isCurrent = plan.planType === organization?.plan;
                const fmt = (n) => (n === -1 ? '∞' : Number(n || 0).toLocaleString('en-IN'));
                const userCount = users.length || 1;
                const total = plan.pricingType === 'per_user' ? plan.price * userCount : plan.price;

                return (
                  <Col xs={24} sm={12} md={8} key={plan._id}>
                    <Card
                      hoverable={!isCurrent}
                      onClick={() => !isCurrent && setSelectedNewPlan(plan)}
                      style={{
                        marginBottom: 16,
                        border: isSelected ? '2px solid #1890ff' : isCurrent ? '2px solid #52c41a' : '1px solid #d9d9d9',
                        cursor: isCurrent ? 'not-allowed' : 'pointer',
                        opacity: isCurrent ? 0.6 : 1,
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '32px', color: plan.metadata?.color || '#1890ff', marginBottom: 8 }}>
                          {plan.metadata?.icon === 'crown' ? <CrownOutlined /> :
                           plan.metadata?.icon === 'star' ? <StarOutlined /> :
                           <SafetyCertificateOutlined />}
                        </div>
                        <Title level={4} style={{ margin: '8px 0' }}>{plan.name}</Title>
                        <Tag style={{ textTransform: 'capitalize', marginBottom: 8 }}>{plan.planType}</Tag>
                        <div>
                          {plan.price === 0 ? (
                            <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#10b981' }}>Free</Text>
                          ) : plan.pricingType === 'per_user' ? (
                            <>
                              <Text style={{ fontSize: 13, color: plan.metadata?.color || '#1890ff' }}>
                                ₹{plan.price.toLocaleString('en-IN')} × {userCount} users
                              </Text>
                              <div>
                                <Text style={{ fontSize: 22, fontWeight: 'bold', color: plan.metadata?.color || '#1890ff' }}>
                                  ₹{total.toLocaleString('en-IN')}
                                </Text>
                                <Text type="secondary" style={{ marginLeft: 4 }}>/{plan.billingPeriod}</Text>
                              </div>
                            </>
                          ) : (
                            <>
                              <Text style={{ fontSize: 22, fontWeight: 'bold', color: plan.metadata?.color || '#1890ff' }}>
                                ₹{Number(plan.price || 0).toLocaleString('en-IN')}
                              </Text>
                              <Text type="secondary" style={{ marginLeft: 4 }}>/{plan.billingPeriod}</Text>
                            </>
                          )}
                        </div>
                        <Space size={[4, 4]} wrap style={{ marginTop: 8, justifyContent: 'center' }}>
                          {isCurrent && <Tag color="success">Current Plan</Tag>}
                          {isSelected && !isCurrent && <Tag color="blue">Selected</Tag>}
                          {plan.isPopular && <Tag color="gold">Popular</Tag>}
                          {plan.planType === 'trial' && plan.trialDays > 0 && (
                            <Tag color="cyan">{plan.trialDays}-day trial</Tag>
                          )}
                          {plan.annualDiscountPercent > 0 && (
                            <Tag color="green">−{plan.annualDiscountPercent}% annual</Tag>
                          )}
                        </Space>
                      </div>
                      <Divider style={{ margin: '12px 0' }} />
                      <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                        <div><strong>Users:</strong> {fmt(plan.limits?.maxUsers)}</div>
                        <div><strong>Leads:</strong> {fmt(plan.limits?.maxLeads)}</div>
                        <div><strong>Deals:</strong> {fmt(plan.limits?.maxDeals)}</div>
                        <div><strong>Storage:</strong> {plan.limits?.maxStorage === -1 ? 'Unlimited' : `${plan.limits?.maxStorage || 0} GB`}</div>
                        <div><strong>Integrations:</strong> {fmt(plan.limits?.maxIntegrations)}</div>
                        {(plan.coupons?.length || 0) > 0 && (
                          <div style={{ marginTop: 4 }}>
                            <strong>Coupons:</strong>{' '}
                            {plan.coupons.slice(0, 2).map((c) => (
                              <Tag key={c._id || c.code} color="purple" style={{ fontSize: 10, marginInlineEnd: 2 }}>
                                {c.code}
                              </Tag>
                            ))}
                            {plan.coupons.length > 2 && <Text type="secondary">+{plan.coupons.length - 2}</Text>}
                          </div>
                        )}
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
            {availablePlans.length === 0 && (
              <Empty description="No subscription plans available" />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrganizationDetail;
